import { endOfDay } from 'date-fns'
import { FC, ReactNode, useEffect, useMemo, useRef } from 'react'
import { SidebarHeader } from 'react-calendar-timeline'

import { Box, Grid } from '@mui/material'

import GRCircularProgress from '../../../../../components/GRCircularProgress/GRCircularProgress'
import { difference } from '../../../../../helpers/difference'
import { isNil } from '../../../../../helpers/isNil'
import { uniq } from '../../../../../helpers/uniq'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/storeHooks'
import { useDebouncedResize } from '../../../../../hooks/useDebouncedResize'
import { useInternalAccessCheck } from '../../../../account/hooks/roleHooks'
import { GameIcon } from '../../../../game'
import GameIconWithCount from '../../../../game/components/GameIcon/GameIconWithCount'
import { EMPTY_CALENDAR_GROUP_ID, useCalendarEventsByEventType } from '../../../hooks/liveEventsCalendarEvents'
import { selectVisibleEventTypeGroups, visibleEventTypeGroupsChanged } from '../../../slices/liveEventsCalendarsSlice'
import { CalendarTimeRangeChangeCallback, CalendarTimeRanges } from '../../../types/Calendar'
import {
  LiveEventsCalendarTimelineType,
  TrackingEventTimelineGroup,
  TrackingEventTimelineItem,
  TrackingEventWithGameTimelineGroup,
} from '../../../types/LiveEvents'
import { TrackedGame } from '../../../types/TrackedGame'
import { FilteredTrackingEventsByGame, TrackingEventTimeRange, TrackingEventsByGame } from '../../../types/TrackingEvents'
import LiveEventsCalendar from '../LiveEventsCalendar'

type LiveEventsCalendarByEventTypeContainerProps = {
  calendarTimeRanges: CalendarTimeRanges
  trackedGames: TrackedGame[]
  trackingEventsByGame: FilteredTrackingEventsByGame
  stats: TrackingEventsByGame['stats']
  allEventsMaxEndByGame?: { [gameId: string]: number }
  loadedEventsTimeRangeByGame?: { [gameId: string]: TrackingEventTimeRange }
  onCalendarTimeRangeChange: CalendarTimeRangeChangeCallback
  onItemClick: (clickedItem: TrackingEventTimelineItem) => void
  onGroupClick: (clickedGroup: TrackingEventTimelineGroup) => void
  onArchetypeClick: (clickedGroup: TrackingEventTimelineGroup) => void
  highlightedEventId?: string
  groupByArchetype?: boolean
  timeStart?: number
  timeEnd?: number
  minZoom?: number
  maxZoom?: number
  isLoading?: boolean
  children?: ReactNode
}

export const calendarId = 'eventCalendar'
const maxDisplayedGamesOnSidebarHeader = 12

export const LiveEventsCalendarByEventTypeContainer: FC<LiveEventsCalendarByEventTypeContainerProps> = ({
  calendarTimeRanges,
  trackedGames,
  trackingEventsByGame,
  stats,
  allEventsMaxEndByGame,
  loadedEventsTimeRangeByGame,
  highlightedEventId,
  groupByArchetype,
  onCalendarTimeRangeChange,
  onItemClick,
  onGroupClick,
  onArchetypeClick,
  timeStart,
  timeEnd,
  minZoom,
  maxZoom,
  isLoading,
}) => {
  const dispatch = useAppDispatch()
  const isInternalUser = useInternalAccessCheck()
  const visibleGroups = useAppSelector(selectVisibleEventTypeGroups)
  const { groups, items } = useCalendarEventsByEventType({ trackingEventsByGame, trackedGames, highlightedEventId, visibleGroups })
  const debouncedResize = useDebouncedResize(50)
  const maxVisibleTrackingEventEndTime = useMemo(() => {
    return items.reduce((max, item) => {
      if (item.type !== LiveEventsCalendarTimelineType.TrackingEvent) {
        return max
      }

      return item.end_time > max ? item.end_time : max
    }, 0)
  }, [items])

  const allEventsMaxEnd = useMemo(() => {
    if (!allEventsMaxEndByGame) return 0
    return Object.values(allEventsMaxEndByGame).reduce((max, val) => (val > max ? val : max), 0)
  }, [allEventsMaxEndByGame])

  const loadedEventsTimeRange = useMemo(() => {
    if (!loadedEventsTimeRangeByGame) return undefined
    const ranges = Object.values(loadedEventsTimeRangeByGame)
    if (ranges.length === 0) return undefined
    return ranges.reduce(
      (acc, r) => ({
        minTimeData: r.minTimeData < acc.minTimeData || acc.minTimeData === 0 ? r.minTimeData : acc.minTimeData,
        maxTimeData: r.maxTimeData > acc.maxTimeData ? r.maxTimeData : acc.maxTimeData,
      }),
      { minTimeData: 0, maxTimeData: 0 }
    )
  }, [loadedEventsTimeRangeByGame])

  const trackingEventTimeRange = useMemo(() => {
    const gameStats = Object.values(stats ?? {})
    if (gameStats.length === 0) return { minTimeData: 0, maxTimeData: 0 }

    const timestamps = gameStats.reduce(
      (acc, gameStat) => {
        if (gameStat) {
          acc.minTimeData = gameStat.trackingStarted < acc.minTimeData || acc.minTimeData === 0 ? gameStat.trackingStarted : acc.minTimeData
          acc.maxTimeData = gameStat.trackingEnded > acc.maxTimeData ? gameStat.trackingEnded : acc.maxTimeData
        }
        return acc
      },
      { minTimeData: 0, maxTimeData: 0 }
    )

    if (!isInternalUser && allEventsMaxEnd > 0) {
      timestamps.maxTimeData = allEventsMaxEnd
    } else if (!isInternalUser && maxVisibleTrackingEventEndTime > 0) {
      timestamps.maxTimeData = maxVisibleTrackingEventEndTime
    } else if (!isInternalUser) {
      const allEvents = Object.values(trackingEventsByGame.events).flat()
      const maxEventEnd = allEvents.reduce((max, e) => {
        const eventEnd = endOfDay(new Date(e.end)).getTime()
        return eventEnd > max ? eventEnd : max
      }, 0)

      if (maxEventEnd > 0) {
        timestamps.maxTimeData = maxEventEnd
      }
    }

    return timestamps
  }, [stats, isInternalUser, allEventsMaxEnd, maxVisibleTrackingEventEndTime, trackingEventsByGame.events])

  const loadedEventsTimeRangeForMarkers = isInternalUser ? loadedEventsTimeRange : trackingEventTimeRange

  // set initial open groups
  useEffect(() => {
    if (!isLoading && isNil(visibleGroups) && groups && groups.length > 0) {
      dispatch(visibleEventTypeGroupsChanged(groups.map((group) => group.eventTypeId)))
    }
  }, [visibleGroups, groups, dispatch, isLoading])

  // Whenever the items or groups provided for the calendar change we trigger a re-render of the calendar by changing the key of the calendar component
  // to reset the visible time range. This aims to fix GR-1101, GR-1618 and GR-1795.
  useEffect(() => {
    debouncedResize()
    return () => debouncedResize.clear()
  }, [debouncedResize, groups, items])

  const sidebarHeader = useSidebarHeader({ groups, isLoading })
  return (
    <LiveEventsCalendar
      trackingEventTimeRange={trackingEventTimeRange}
      groups={groups}
      items={isLoading ? [] : items}
      onItemClick={onItemClick}
      onGroupClick={onGroupClick}
      onArchetypeClick={onArchetypeClick}
      sidebarHeader={sidebarHeader}
      timeStart={calendarTimeRanges[calendarId]?.timeStart || timeStart}
      timeEnd={calendarTimeRanges[calendarId]?.timeEnd || timeEnd}
      minZoom={minZoom}
      maxZoom={maxZoom}
      isLoading={isLoading}
      calendarHidden={false}
      lineHeight={34}
      onCalendarTimeRangeChange={(timeStart, timeEnd) => onCalendarTimeRangeChange(calendarId, timeStart, timeEnd)}
      showEmptyCalendarIndicator={!!(visibleGroups && visibleGroups.length > 0) || !!(groups.length === 1 && groups[0]?.id === EMPTY_CALENDAR_GROUP_ID)}
      loadedEventsTimeRange={loadedEventsTimeRangeForMarkers}
    />
  )
}

const useSidebarHeader = ({ groups, isLoading }: { groups: TrackingEventWithGameTimelineGroup[]; isLoading?: boolean }) => {
  const trackedGamesWithEventsRef = useRef<TrackedGame[]>([])
  const trackedGamesWithEvents = useMemo(() => {
    const games = uniq(groups.flatMap((group) => group.trackedGamesOfEventType || []))
    const diff = difference(games, trackedGamesWithEventsRef.current)

    if (diff.length > 0 || games.length !== trackedGamesWithEventsRef.current.length) {
      trackedGamesWithEventsRef.current = games
      return games
    } else {
      return trackedGamesWithEventsRef.current
    }
  }, [groups])

  return useMemo(
    () => (
      <SidebarHeader>
        {({ getRootProps }: any) => (
          <Grid container wrap="wrap" gap={0.5} pt={1} pl={1} alignItems="flex-start" {...getRootProps()}>
            {isLoading && (
              <Box flex={1} alignSelf="center">
                <GRCircularProgress />
              </Box>
            )}

            {!isLoading &&
              trackedGamesWithEvents.length > maxDisplayedGamesOnSidebarHeader &&
              trackedGamesWithEvents.slice(0, 11).map((trackedGame) => {
                return (
                  trackedGame && (
                    <GameIcon
                      gameName={trackedGame.game.resolvedName}
                      src={trackedGame.game.getIcon()}
                      size="small"
                      key={trackedGame.game.id}
                      appType={trackedGame.game.appType}
                      gamePlatforms={trackedGame.game.platforms}
                      discontinued={trackedGame.discontinued}
                    />
                  )
                )
              })}

            {!isLoading && trackedGamesWithEvents.length > maxDisplayedGamesOnSidebarHeader && (
              <GameIconWithCount count={trackedGamesWithEvents.length - (maxDisplayedGamesOnSidebarHeader - 1)} prefix="+" size="small" />
            )}

            {!isLoading &&
              trackedGamesWithEvents.length <= maxDisplayedGamesOnSidebarHeader &&
              trackedGamesWithEvents.map((trackedGame) => {
                return (
                  trackedGame && (
                    <GameIcon
                      gameName={trackedGame.game.resolvedName}
                      src={trackedGame.game.getIcon()}
                      size="small"
                      key={trackedGame.game.id}
                      appType={trackedGame.game.appType}
                      gamePlatforms={trackedGame.game.platforms}
                      discontinued={trackedGame.discontinued}
                    />
                  )
                )
              })}
          </Grid>
        )}
      </SidebarHeader>
    ),
    [trackedGamesWithEvents, isLoading]
  )
}
